import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Image,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Popconfirm,
  Avatar,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  StarOutlined,
  StarFilled,
  CheckCircleOutlined,
  StopOutlined,
  CrownOutlined,
  UserOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import petsService, { Pet } from '../services/petsService';
import { getImageUrl } from '../config/api';

const { Title, Text, Paragraph } = Typography;

const nameOf = (ref?: { name?: string; title?: string } | string | null): string => {
  if (!ref) return '-';
  if (typeof ref === 'string') return ref;
  return ref.name || ref.title || '-';
};

const imageUrls = (pet: Pet): string[] => {
  if (!pet.images || pet.images.length === 0) return [];
  return pet.images
    .map((img) => (typeof img === 'string' ? getImageUrl(img) : getImageUrl(img.url || img.image || '')))
    .filter(Boolean);
};

const PetDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pet, setPet] = useState<Pet | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchPet = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await petsService.getPetById(id);
      setPet(data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to load pet');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPet();
  }, [fetchPet]);

  const setFlag = async (
    flag: 'isAvailable' | 'isApproved' | 'isFeatured' | 'isPremium',
    value: boolean,
    label: string,
  ) => {
    if (!pet) return;
    try {
      setSaving(true);
      await petsService.setPetFlag(pet._id, flag, value);
      message.success(`${label} ${value ? 'enabled' : 'disabled'}`);
      fetchPet();
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || `Failed to update ${label}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pet) return;
    try {
      await petsService.deletePet(pet._id);
      message.success('Pet listing deleted');
      navigate('/pets');
    } catch (error: any) {
      message.error(error?.response?.data?.message || error.message || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!pet) {
    return (
      <div style={{ padding: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/pets')}>
          Back to Pet Listings
        </Button>
        <div style={{ marginTop: 24 }}>
          <Text type="secondary">Pet not found.</Text>
        </div>
      </div>
    );
  }

  const imgs = imageUrls(pet);
  const owner: any = pet.owner;
  const ageStr =
    pet.age && typeof pet.age === 'object'
      ? `${(pet.age as any).value ?? ''} ${(pet.age as any).unit ?? ''}`.trim()
      : (pet.age != null ? String(pet.age) : '-');

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/pets')}>
          Back to Pet Listings
        </Button>
        <Button icon={<ReloadOutlined />} onClick={fetchPet}>
          Refresh
        </Button>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={10}>
          <Card>
            {imgs.length > 0 ? (
              <Image.PreviewGroup>
                <Image
                  src={imgs[0]}
                  width="100%"
                  style={{ borderRadius: 8, objectFit: 'cover', maxHeight: 360 }}
                />
                <Space wrap style={{ marginTop: 8 }}>
                  {imgs.slice(1).map((u, i) => (
                    <Image key={i} src={u} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 6 }} />
                  ))}
                </Space>
              </Image.PreviewGroup>
            ) : (
              <div style={{ height: 240, background: '#f0f0f0', borderRadius: 8 }} />
            )}
          </Card>
        </Col>

        <Col xs={24} md={14}>
          <Card>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Title level={3} style={{ margin: 0 }}>{pet.title}</Title>
              <Title level={4} style={{ margin: 0, color: '#c8431f' }}>
                ₹{pet.price?.toLocaleString?.() ?? pet.price}
                {pet.isNegotiable ? <Tag color="blue" style={{ marginLeft: 8 }}>Negotiable</Tag> : null}
              </Title>
              <Space wrap size={4} style={{ marginTop: 6 }}>
                {pet.isAvailable ? <Tag color="green">Available</Tag> : <Tag color="red">Sold / Hidden</Tag>}
                {pet.isApproved ? <Tag color="cyan">Approved</Tag> : <Tag color="orange">Pending</Tag>}
                {pet.isFeatured ? <Tag color="gold">Featured</Tag> : null}
                {pet.isPremium ? <Tag color="purple">Premium</Tag> : null}
              </Space>
            </Space>

            {/* Moderation actions */}
            <div style={{ marginTop: 16 }}>
              <Space wrap>
                <Button
                  loading={saving}
                  icon={pet.isAvailable ? <StopOutlined /> : <CheckCircleOutlined />}
                  onClick={() => setFlag('isAvailable', !pet.isAvailable, 'Availability')}
                >
                  {pet.isAvailable ? 'Mark Sold / Hide' : 'Make Available'}
                </Button>
                <Button
                  loading={saving}
                  icon={pet.isApproved ? <StopOutlined /> : <CheckCircleOutlined />}
                  type={pet.isApproved ? 'default' : 'primary'}
                  onClick={() => setFlag('isApproved', !pet.isApproved, 'Approval')}
                >
                  {pet.isApproved ? 'Unapprove' : 'Approve'}
                </Button>
                <Button
                  loading={saving}
                  icon={pet.isFeatured ? <StarFilled style={{ color: '#f6b100' }} /> : <StarOutlined />}
                  onClick={() => setFlag('isFeatured', !pet.isFeatured, 'Featured')}
                >
                  {pet.isFeatured ? 'Unfeature' : 'Feature'}
                </Button>
                <Button
                  loading={saving}
                  icon={<CrownOutlined />}
                  onClick={() => setFlag('isPremium', !pet.isPremium, 'Premium')}
                >
                  {pet.isPremium ? 'Remove Premium' : 'Make Premium'}
                </Button>
                <Popconfirm
                  title="Delete this pet listing?"
                  description="This cannot be undone."
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={handleDelete}
                >
                  <Button danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              </Space>
            </div>

            <Descriptions column={1} size="small" style={{ marginTop: 20 }} bordered>
              <Descriptions.Item label="Category">{nameOf(pet.petCategory as any)}</Descriptions.Item>
              <Descriptions.Item label="Breed">{nameOf(pet.breed as any)}</Descriptions.Item>
              <Descriptions.Item label="Age">{ageStr}</Descriptions.Item>
              <Descriptions.Item label="Gender">{pet.gender || '-'}</Descriptions.Item>
              <Descriptions.Item label="Location">{pet.address || '-'}</Descriptions.Item>
              <Descriptions.Item label="Views">{pet.viewCount ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Listed">
                {pet.createdAt ? new Date(pet.createdAt).toLocaleString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Owner">
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  <span>{nameOf(owner)}</span>
                  <Text type="secondary">
                    {typeof owner === 'object' ? owner?.phone || owner?.email || '' : ''}
                  </Text>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            {pet.description ? (
              <div style={{ marginTop: 16 }}>
                <Text strong>Description</Text>
                <Paragraph style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{pet.description}</Paragraph>
              </div>
            ) : null}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PetDetail;
